/**
 * Offline-First Mutation Outbox (IndexedDB)
 *
 * Race day at the field has spotty cell + WiFi. Operator taps
 * (markLap, markCut, Go, End Heat, etc.) need to land in the UI
 * the millisecond they happen — never blocked on a network call,
 * never lost when the connection flakes. This module is the
 * shared outbox every write path enqueues into:
 *
 *   1. Caller computes the optimistic local effect immediately.
 *   2. Caller `enqueue(...)`s the corresponding HTTP mutation.
 *   3. The flusher drains the queue against the server in the
 *      background. Retries with backoff. Restarts on tab focus,
 *      online events, and a periodic timer so a phone that came
 *      back into range catches up automatically.
 *   4. Successful writes drop from the queue. Server-side
 *      idempotency (deterministic doc ids on lap-events,
 *      Firestore patches that overwrite the same field) means
 *      duplicate POSTs are safe — the queue can be aggressive
 *      about retrying without poisoning state.
 *
 * Responsiveness is the design goal — the UI never awaits the
 * network. Sync is best-effort and continuous.
 *
 * Bearer-auth entries fetch a fresh Firebase ID token at flush
 * time (tokens expire after an hour) — the queue never stores
 * tokens. Counter-token entries store the HMAC token verbatim
 * since those tokens are race-event-scoped and don't rotate.
 *
 * Copyright — see the LICENSE file in the repository root for terms.
 */

import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'srac-outbox';
const DB_VERSION = 1;
const STORE = 'mutations';

/** Stable identifier for the mutation kind — used by status UI
 *  to render human-readable strings, and by callers to match
 *  pending entries against optimistic state for reconciliation. */
export type OutboxKind =
  | 'lap-event' // race-scoped POST /api/competitions/races/:raceId/lap-events
  | 'cut-event-create' // POST /api/competitions/heats/:heatId/cut-events
  | 'cut-event-revert' // PATCH /api/competitions/heats/:heatId/cut-events/:eventId
  | 'heat-patch' // PATCH /api/competitions/heats/:heatId  (start, stop, results)
  | 'heat-stop' // POST /api/competitions/races/:raceId/control/stop-heat
  | 'judge-update' // arbitrary judge-app PATCH; provide kind for status display
  | 'generic'; // fallback for one-off callers

export type OutboxAuthMode = 'bearer' | 'counter' | 'none';

export interface OutboxEntry {
  /** Unique id assigned at enqueue. crypto.randomUUID() under the hood. */
  id: string;
  kind: OutboxKind;
  /** HTTP target. Resolved against the page origin. */
  url: string;
  method: 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  /** JSON-stringified body (or null for empty bodies). */
  bodyJson: string | null;
  /** Authorization mode resolved at flush time. */
  authMode: OutboxAuthMode;
  /** HMAC counter token when authMode='counter'. */
  counterToken?: string;
  /** Caller-supplied client-correlation id so optimistic UI can
   *  reconcile against the eventual server response. */
  correlationId?: string;
  /** Caller-supplied free-form metadata (e.g. heatId, pilotId)
   *  surfaced in status UIs. */
  meta?: Record<string, unknown>;
  /** Wall-clock millis when the entry was enqueued. */
  createdAt: number;
  /** Wall-clock millis of the last flush attempt (success or fail). */
  lastAttemptAt?: number;
  /** Number of flush attempts so far. */
  attempts: number;
  /** Most recent server error message — surfaced for diagnostics. */
  lastError?: string;
  /** Lifecycle state. */
  status: 'pending' | 'inflight' | 'failed';
}

export interface EnqueueArgs {
  kind: OutboxKind;
  url: string;
  method?: 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  authMode: OutboxAuthMode;
  counterToken?: string;
  correlationId?: string;
  meta?: Record<string, unknown>;
}

let dbPromise: Promise<IDBPDatabase> | null = null;
function getDb(): Promise<IDBPDatabase> | null {
  if (typeof window === 'undefined') return null;
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'id' });
          store.createIndex('byCreatedAt', 'createdAt');
        }
      },
    });
  }
  return dbPromise;
}

const subscribers = new Set<() => void>();
function notify() {
  for (const fn of subscribers) {
    try {
      fn();
    } catch {
      /* swallow — broken subscriber should not break others */
    }
  }
}

/**
 * Subscribe to outbox state changes. Fires on enqueue / status
 * change / drain. Returns the unsubscribe function.
 */
export function subscribe(fn: () => void): () => void {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

function uuid(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Append a mutation to the outbox and trigger a flush. Returns
 * the entry id so callers can correlate it back to optimistic
 * UI state (e.g. mark a lap "synced" once the entry drops).
 */
export async function enqueue(args: EnqueueArgs): Promise<string> {
  const id = uuid();
  const now = Date.now();
  const entry: OutboxEntry = {
    id,
    kind: args.kind,
    url: args.url,
    method: args.method ?? 'POST',
    bodyJson:
      args.body === undefined || args.body === null
        ? null
        : JSON.stringify(args.body),
    authMode: args.authMode,
    counterToken: args.counterToken,
    correlationId: args.correlationId,
    meta: args.meta,
    createdAt: now,
    attempts: 0,
    status: 'pending',
  };
  const dbp = getDb();
  if (dbp) {
    try {
      const db = await dbp;
      await db.put(STORE, entry);
    } catch {
      /* IDB unavailable — the in-memory subscribers still fire so
         the UI updates; the entry just won't survive a refresh.
         Operator can retry the action. */
    }
  }
  notify();
  // Kick a flush asynchronously — the caller doesn't wait.
  void scheduleFlush();
  return id;
}

/** Read the current contents of the outbox (oldest first). */
export async function list(): Promise<OutboxEntry[]> {
  const dbp = getDb();
  if (!dbp) return [];
  try {
    const db = await dbp;
    const all = (await db.getAll(STORE)) as OutboxEntry[];
    all.sort((a, b) => a.createdAt - b.createdAt);
    return all;
  } catch {
    return [];
  }
}

/** Remove a specific entry — used when a write succeeds. */
export async function remove(id: string): Promise<void> {
  const dbp = getDb();
  if (!dbp) return;
  try {
    const db = await dbp;
    await db.delete(STORE, id);
  } catch {
    /* ignore */
  }
  notify();
}

async function update(entry: OutboxEntry): Promise<void> {
  const dbp = getDb();
  if (!dbp) return;
  try {
    const db = await dbp;
    await db.put(STORE, entry);
  } catch {
    /* ignore */
  }
  notify();
}

/**
 * Token providers. Caller-supplied so the outbox doesn't have a
 * hard dep on Firebase. Setters are called once at app boot
 * (e.g. from a top-level provider) before any enqueue happens.
 */
type BearerProvider = () => Promise<string | null>;
let bearerProvider: BearerProvider | null = null;
export function setBearerProvider(fn: BearerProvider): void {
  bearerProvider = fn;
}

let onlineFlag = true;
if (typeof window !== 'undefined') {
  onlineFlag = navigator.onLine;
  window.addEventListener('online', () => {
    onlineFlag = true;
    void scheduleFlush();
  });
  window.addEventListener('offline', () => {
    onlineFlag = false;
    notify();
  });
  window.addEventListener('focus', () => void scheduleFlush());
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void scheduleFlush();
  });
}

let flushScheduled = false;
let flushInFlight = false;

/**
 * Asynchronously trigger a flush. Coalesces to one in-flight
 * worker — repeated calls during a flush queue up a single
 * follow-up pass.
 */
export function scheduleFlush(): void {
  if (flushInFlight) {
    flushScheduled = true;
    return;
  }
  flushScheduled = false;
  flushInFlight = true;
  void (async () => {
    try {
      await flushOnce();
    } finally {
      flushInFlight = false;
      if (flushScheduled) {
        flushScheduled = false;
        scheduleFlush();
      }
    }
  })();
}

/**
 * Drain the outbox once. Each entry is sent in createdAt order so
 * later writes don't land before earlier ones (e.g. lap N+1
 * doesn't beat lap N to the server). Failed entries get a backoff
 * window and are retried by the next call.
 */
async function flushOnce(): Promise<void> {
  if (!onlineFlag && typeof navigator !== 'undefined' && !navigator.onLine) {
    return;
  }
  const entries = await list();
  for (const entry of entries) {
    if (entry.status === 'inflight') continue;
    // Backoff: cap at 30 s. attempts^2 seconds.
    if (entry.lastAttemptAt) {
      const wait = Math.min(30_000, entry.attempts * entry.attempts * 1000);
      if (Date.now() - entry.lastAttemptAt < wait) continue;
    }
    entry.status = 'inflight';
    entry.attempts += 1;
    entry.lastAttemptAt = Date.now();
    await update(entry);
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (entry.authMode === 'bearer' && bearerProvider) {
        const token = await bearerProvider();
        if (token) headers.Authorization = `Bearer ${token}`;
      }
      // Counter-token entries either go in the body (existing
      // race-scoped lap-events route) or as an Authorization
      // "Counter <token>" header (pilot photos endpoint). Both
      // are pre-baked into the body / headers by the caller —
      // the outbox just transports.
      const res = await fetch(entry.url, {
        method: entry.method,
        headers,
        body: entry.bodyJson,
        keepalive: true,
      });
      if (res.ok) {
        await remove(entry.id);
        continue;
      }
      // 4xx (other than 408 / 429) → permanent failure. Stop
      // retrying so the operator sees a useful error in the
      // status UI. 5xx + network failures stay in retry mode.
      if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
        const body = await res.text().catch(() => '');
        entry.status = 'failed';
        entry.lastError = `${res.status} ${body.slice(0, 200)}`;
        await update(entry);
        continue;
      }
      const body = await res.text().catch(() => '');
      entry.status = 'pending';
      entry.lastError = `${res.status} ${body.slice(0, 200)}`;
      await update(entry);
    } catch (err) {
      entry.status = 'pending';
      entry.lastError = err instanceof Error ? err.message : String(err);
      await update(entry);
    }
  }
}

if (typeof window !== 'undefined') {
  // Wake the queue on a slow tick so a stuck retry that hit its
  // backoff still gets a chance to fire while the page is open.
  setInterval(() => void scheduleFlush(), 10_000);
}
